/**
 * Data hook for chat user operations (add/remove users)
 * Separates GraphQL operations from UI components.
 *
 * Both operations resolve with the chat's full, server-true user list taken
 * from the mutation response (`chatInviteUsers` / `chatRemoveUsers` return the
 * updated Chat). Callers write that list straight into the chat page context so
 * the participants view reflects the change immediately without an extra
 * round-trip. The mutation response also normalises into the Apollo cache under
 * the same `Chat:{id}.users(first:$first)` key that `GetChatData` reads, so the
 * other tabs stay consistent — no `refetchQueries` is needed.
 */

import type { ChatUserOperationInput, ChatUser } from '@choco-admin/types';
import { useCallback, useState } from 'react';

import {
  useAddChatUsersMutation,
  useRemoveChatUsersMutation,
  AddChatUsersMutation,
  RemoveChatUsersMutation,
} from '../../../generated';
import { CHAT_USERS_PAGE_SIZE } from '../constants/chatConstants';

// Map the chat returned by an add/remove mutation to the domain user list.
const mapResponseUsers = (
  chat:
    | NonNullable<AddChatUsersMutation['chatInviteUsers']>
    | NonNullable<RemoveChatUsersMutation['chatRemoveUsers']>
): ChatUser[] => {
  if (!chat.users.edges) {
    throw new Error('Chat user mutation returned no user list');
  }

  return chat.users.edges.map((edge) => ({
    id: edge.node.id,
    name: edge.node.name || '',
    email: edge.node.email || undefined,
    phone: edge.node.phone,
    supplier: edge.node.supplier,
    // Preserve the bot flag so the reconciled list keeps excluding the supplier
    // bot user from participant/notification rows (it is `supplier: true` too).
    botUser: edge.node.botUser || false,
    orderPreferences: {
      whatsApp: Boolean(edge.node.orderPreference?.whatsApp),
      sms: Boolean(edge.node.orderPreference?.sms),
      fax: Boolean(edge.node.orderPreference?.fax),
    },
  }));
};

const getSingleChatId = (input: ChatUserOperationInput[]): string => {
  const chatIds = new Set(input.map((operation) => operation.chatId));

  if (chatIds.size !== 1) {
    throw new Error('Chat user operations must target a single chat');
  }

  return input[0].chatId;
};

export interface UseChatUserOperationsResponse {
  addUsers: (input: ChatUserOperationInput[]) => Promise<ChatUser[]>;
  removeUsers: (input: ChatUserOperationInput[]) => Promise<ChatUser[]>;
  isLoading: boolean;
  error: Error | null;
}

export const useChatUserOperations = (): UseChatUserOperationsResponse => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [addChatUsersMutation] = useAddChatUsersMutation({
    errorPolicy: 'none',
  });
  const [removeChatUsersMutation] = useRemoveChatUsersMutation({
    errorPolicy: 'none',
  });

  const addUsers = useCallback(
    async (input: ChatUserOperationInput[]): Promise<ChatUser[]> => {
      if (input.length === 0) return [];

      setIsLoading(true);
      setError(null);

      try {
        const chatId = getSingleChatId(input);
        const userIds = input.map((operation) => operation.userId);

        const result = await addChatUsersMutation({
          variables: {
            id: chatId,
            userIds,
            first: CHAT_USERS_PAGE_SIZE,
            withAllUserFields: true,
          },
        });

        const chat = result.data?.chatInviteUsers;
        if (!chat) {
          throw new Error('Chat user mutation returned no chat data');
        }

        return mapResponseUsers(chat);
      } catch (err) {
        const addError = err as Error;
        setError(addError);
        throw addError;
      } finally {
        setIsLoading(false);
      }
    },
    [addChatUsersMutation]
  );

  const removeUsers = useCallback(
    async (input: ChatUserOperationInput[]): Promise<ChatUser[]> => {
      if (input.length === 0) return [];

      setIsLoading(true);
      setError(null);

      try {
        const chatId = getSingleChatId(input);
        const userIds = input.map((operation) => operation.userId);

        const result = await removeChatUsersMutation({
          variables: {
            id: chatId,
            userIds,
            first: CHAT_USERS_PAGE_SIZE,
            withAllUserFields: true,
          },
        });

        const chat = result.data?.chatRemoveUsers;
        if (!chat) {
          throw new Error('Chat user mutation returned no chat data');
        }

        return mapResponseUsers(chat);
      } catch (err) {
        const removeError = err as Error;
        setError(removeError);
        throw removeError;
      } finally {
        setIsLoading(false);
      }
    },
    [removeChatUsersMutation]
  );

  return {
    addUsers,
    removeUsers,
    isLoading,
    error,
  };
};
